/**
 * Encrypted, chunked dataset upload — client-side envelope encryption
 * (schema "tanuh-enc-dataset-v1"):
 *
 * 1. SHA-256 hash the plaintext file in 16 MB streaming chunks
 * 2. Generate a random per-file AES-256-GCM DEK; wrap it with the platform's
 *    Cloud KMS RSA-OAEP-3072-SHA256 public key (fetched via files-connect-api)
 * 3. Encrypt chunk i with IV = baseIV + i and AAD "i:totalChunks:fileId",
 *    then PUT it as multipart part i+1 (one ~16 MB ciphertext in memory at a time)
 * 4. On complete-upload the fileserver composes the ciphertext parts in part
 *    order into one object; a manifest sidecar carries everything the TEE
 *    needs to unwrap the DEK (KMS asymmetricDecrypt) and decrypt chunk-wise.
 *
 * Chunk boundaries in the composed object are pure arithmetic — every chunk's
 * ciphertext is its plaintext length + 16-byte GCM tag — so no framing bytes
 * are stored. Ordering is enforced cryptographically by the AAD: decryption
 * fails if chunks are reordered, substituted, or truncated.
 *
 * Same cipher/IV/AAD scheme as the ONNX transfer path (index.ts uploadEncrypted),
 * but streaming per-part instead of one buffered PUT, and KMS-wrapped instead
 * of HPKE-wrapped so the ciphertext is decryptable at rest, not just in-session.
 */

import { sha256 as nobleSha256 } from "@noble/hashes/sha2.js"
import { b64encode, b64decode } from "./util/base64"

export const DATASET_CHUNK_SIZE = 16 * 1024 * 1024  // 16 MB plaintext per AES-GCM chunk
export const GCM_TAG_BYTES = 16
export const ENC_DATASET_SCHEMA = "tanuh-enc-dataset-v1"
export const DEK_WRAP_ALG = "RSA-OAEP-3072-SHA256"

const PART_PUT_ATTEMPTS = 3

/** Sidecar JSON stored as `${key}.manifest.json` — the TEE decryption contract. */
export interface EncryptedDatasetManifest {
    schema:           typeof ENC_DATASET_SCHEMA
    file_id:          string
    cipher:           "AES-256-GCM"
    chunk_size:       number
    total_chunks:     number
    plaintext_size:   number
    plaintext_sha256: string
    base_iv:          string   // b64
    aad_scheme:       "i:totalChunks:fileId"
    wrapped_dek:      string   // b64
    dek_wrap_alg:     typeof DEK_WRAP_ALG
    kms_key_version:  string
    created_at:       string   // ISO 8601
}

export interface EncryptChunksOptions {
    file:          File
    /** PEM (SPKI "PUBLIC KEY") from GET /v1/encryption/public-key */
    publicKeyPem:  string
    /** Full KMS key-version resource name, recorded verbatim in the manifest */
    kmsKeyVersion: string
    /** Upload one encrypted chunk (0-based index); caller maps to part number/URL */
    putPart:       (chunkIndex: number, bytes: Uint8Array) => Promise<void>
    /** 0–100 across all chunks */
    onProgress?:   (pct: number) => void
}

/** Streaming SHA-256 of a File (peak memory: one 16 MB chunk). */
export async function computeFileSha256(
    file: File,
    onProgress?: (pct: number) => void,
): Promise<string> {
    const hasher = nobleSha256.create()
    let offset = 0
    while (offset < file.size) {
        const buf = await file.slice(offset, offset + DATASET_CHUNK_SIZE).arrayBuffer()
        hasher.update(new Uint8Array(buf))
        offset += DATASET_CHUNK_SIZE
        onProgress?.(Math.min(100, Math.round(offset / file.size * 100)))
    }
    const digest = hasher.digest()
    return Array.from(digest).map(b => b.toString(16).padStart(2, "0")).join("")
}

/** Parse a PEM SPKI public key and import it for RSA-OAEP-SHA256 wrapping. */
export async function importWrappingKey(publicKeyPem: string): Promise<CryptoKey> {
    const b64 = publicKeyPem
        .replace(/-----BEGIN PUBLIC KEY-----/, "")
        .replace(/-----END PUBLIC KEY-----/, "")
        .replace(/\s+/g, "")
    const spki = b64decode(b64)
    return crypto.subtle.importKey(
        "spki", spki, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"],
    )
}

/** Number of multipart parts an encrypted upload of `fileSize` bytes needs. */
export function encryptedPartCount(fileSize: number): number {
    return Math.max(1, Math.ceil(fileSize / DATASET_CHUNK_SIZE))
}

/**
 * Encrypt the file chunk-by-chunk, handing each ciphertext to `putPart`.
 * Returns the manifest to upload as `${key}.manifest.json` after the
 * multipart upload has been completed (composed) successfully.
 */
export async function encryptAndUploadChunks(opts: EncryptChunksOptions): Promise<EncryptedDatasetManifest> {
    const { file, putPart, onProgress } = opts
    const totalChunks = encryptedPartCount(file.size)
    const fileId      = crypto.randomUUID()

    const sha256hex = await computeFileSha256(file)

    // Fresh DEK per file; wrapped copy is the only durable form of the key.
    const rawKey = crypto.getRandomValues(new Uint8Array(32))
    const aesKey = await crypto.subtle.importKey(
        "raw", rawKey, { name: "AES-GCM" }, false, ["encrypt"],
    )
    const wrappingKey = await importWrappingKey(opts.publicKeyPem)
    const wrappedDek  = new Uint8Array(await crypto.subtle.encrypt(
        { name: "RSA-OAEP" }, wrappingKey, rawKey,
    ))
    rawKey.fill(0)

    // 12-byte base IV; chunk i uses baseIV + i (96-bit big-endian increment).
    const baseIV = crypto.getRandomValues(new Uint8Array(12))
    const iv     = new Uint8Array(baseIV)

    for (let i = 0; i < totalChunks; i++) {
        const plaintext = await file.slice(
            i * DATASET_CHUNK_SIZE, (i + 1) * DATASET_CHUNK_SIZE,
        ).arrayBuffer()

        const aad = new TextEncoder().encode(`${i}:${totalChunks}:${fileId}`)

        const ct = new Uint8Array(await crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv.slice(), additionalData: aad, tagLength: 128 },
            aesKey,
            plaintext,
        ))

        let lastErr: unknown
        for (let attempt = 1; attempt <= PART_PUT_ATTEMPTS; attempt++) {
            try {
                await putPart(i, ct)
                lastErr = undefined
                break
            } catch (err) {
                lastErr = err
                if (attempt < PART_PUT_ATTEMPTS) {
                    await new Promise(r => setTimeout(r, attempt * 1000))
                }
            }
        }
        if (lastErr !== undefined) {
            throw new Error(`chunk ${i + 1}/${totalChunks} upload failed: ${String(lastErr)}`)
        }

        onProgress?.(Math.round((i + 1) / totalChunks * 100))
        // increment IV by 1 (big-endian, with carry) for the next chunk.
        // NOTE: `++iv[j] !== 0` does NOT work on typed arrays — the ++ expression
        // evaluates to the unwrapped 256, so the carry would never propagate.
        for (let j = 11; j >= 0; j--) {
            iv[j] = (iv[j] + 1) & 0xff
            if (iv[j] !== 0) break
        }
    }

    return {
        schema:           ENC_DATASET_SCHEMA,
        file_id:          fileId,
        cipher:           "AES-256-GCM",
        chunk_size:       DATASET_CHUNK_SIZE,
        total_chunks:     totalChunks,
        plaintext_size:   file.size,
        plaintext_sha256: sha256hex,
        base_iv:          b64encode(baseIV),
        aad_scheme:       "i:totalChunks:fileId",
        wrapped_dek:      b64encode(wrappedDek),
        dek_wrap_alg:     DEK_WRAP_ALG,
        kms_key_version:  opts.kmsKeyVersion,
        created_at:       new Date().toISOString(),
    }
}

/** Object key of the manifest sidecar for a given dataset object key. */
export function manifestKeyFor(objectKey: string): string {
    return `${objectKey}.manifest.json`
}
